import React from "react";
import { Box, Stack, Fade } from "@mui/material";
import liff from "@line/liff";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useMyPageData from "works/higurashi/hooks/useMyPageData";
import useNextPhase from "works/higurashi/hooks/useNextPhase";
import useResetMemory from "hooks/useResetMemory";

import Loading from "default/components/Loading";
import StyledButton from "default/components/StyledButton";
import MyPageCard from "default/components/MyPageCard";

import AppLayout from "works/higurashi/components/AppLayout";
import Error from "default/components/Error";
import TicketNotFound from "default/components/TicketNotFound";
import Header from "works/higurashi/components/Header";
import NextPhaseSection from "works/higurashi/components/NextPhaseSection";
import ResetMemorySection from "works/higurashi/components/ResetMemorySection";
import OtherCharactersButton from "works/higurashi/components/OtherCharactersButton";
import { buildHigurashiMyPageCardData } from "works/higurashi/utils/myPageCard";
import { getOtherHigurashiSouls } from "works/higurashi/utils/characters";
import { getRequestFormUrl } from "constants/links";

import { useBasicInfo } from "works/higurashi/hooks/useBasicInfo";

// Helper functions
const withLng = (path, lng) => {
  const hasQuery = path.includes("?");
  return lng ? `${path}${hasQuery ? "&" : "?"}lng=${lng}` : path;
};

const EndInterludeSection = ({ chapter, t }) => (
  <Box
    sx={{
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 3,
    }}
  >
    <Header
      title="Interlude"
      subtitle={t(`chapter.${chapter}`)}
      highlightedWord="d"
    />
    <Box sx={{ width: "100%", px: 2, boxSizing: "border-box" }}>
      <Box
        component="p"
        sx={{
          whiteSpace: "pre-line",
          textAlign: "center",
          lineHeight: 2.4,
          fontWeight: 600,
          fontSize: 17,
          fontFamily: "'Noto Serif JP', serif",
          m: 0,
        }}
      >
        {t(`chapterStory.${chapter}.interlude`)}
      </Box>
    </Box>
  </Box>
);

function MyPage() {
  const navigate = useNavigate();
  const {
    work,
    chapter,
    lng,
    character,
    visualStyle,
    accountKey,
    state,
  } = useBasicInfo();
  const { t } = useTranslation([work, "common"]);
  const requestFormUrl = getRequestFormUrl(lng);

  const {
    isLoading: isMyPageLoading,
    isError: isMyPageError,
    data: myPageData,
    isExpired,
    canProceedToNextPhase,
    nextPhaseControl,
    canReset,
    refetch: refetchMyPageData,
  } = useMyPageData(accountKey);
  const isEnded = myPageData?.is_ended === true;

  const resetMemory = useResetMemory(canReset, isExpired);
  const nextPhase = useNextPhase(accountKey, refetchMyPageData);
  const cardData = buildHigurashiMyPageCardData({
    stateData: myPageData,
    basicState: state,
    character,
    visualStyle,
    t,
    publicUrl: process.env.PUBLIC_URL,
  });
  const currentPhase = myPageData?.stories?.current?.phase;
  const canShowManualAdvanceButton =
    currentPhase === "1" && canProceedToNextPhase && Boolean(nextPhaseControl);
  const otherSouls = getOtherHigurashiSouls({ character, visualStyle, lng });

  // Navigation handlers
  const handleNavigateHowToUse = () => {
    navigate(withLng(`/${work}/how-to-use?account=${accountKey}`, lng));
  };

  const handleNavigateTickets = () => {
    navigate(withLng(`/${work}/tickets?account=${accountKey}`, lng));
  };

  const handleNavigateQuests = () => {
    navigate(withLng(`/${work}/quests?account=${accountKey}`, lng));
  };

  const handleNavigateEmailSettings = () => {
    navigate(withLng(`/${work}/email-settings?account=${accountKey}`, lng));
  };

  const handleCloseWindow = () => {
    liff.closeWindow();
  };

  const handleRequestForm = () => {
    window.open(requestFormUrl, "_blank");
  };

  const onPromote = () => {
    navigate(withLng(`/${work}/tickets?account=${accountKey}`, lng));
  };

  if (isMyPageLoading) {
    return (
      <AppLayout page="MyPage">
        <Loading />
      </AppLayout>
    );
  }

  if (isMyPageError) {
    return (
      <AppLayout page="MyPage">
        <Error />
      </AppLayout>
    );
  }

  if (!state || !cardData) {
    return (
      <AppLayout page="MyPage">
        <TicketNotFound onClick={onPromote} />
      </AppLayout>
    );
  }

  return (
    <AppLayout page="MyPage" width="100%">
      <Box
        sx={{
          display: "flex",
          paddingTop: 4,
          paddingBottom: 4,
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Header */}
        <Fade in timeout={600}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Header
              title="My Page"
              highlightedWord="P"
              subtitle={t(`chapter.${chapter}`)}
              sx={{ marginBottom: 6 }}
            />
          </Box>
        </Fade>

        <Fade in timeout={800} style={{ transitionDelay: "200ms" }}>
          <Box
            sx={{
              width: "100%",
              maxWidth: 440,
              px: 2,
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <MyPageCard
              userEpisode={cardData}
              soulName={character}
              displayRelationship={false}
            />
          </Box>
        </Fade>

        {/* Button Sections */}
        <Fade in timeout={1000} style={{ transitionDelay: "400ms" }}>
          <Stack
            spacing={4}
            sx={{ mt: 4, width: "80%", maxWidth: 400, alignItems: "center" }}
          >
            {isEnded && (
              <EndInterludeSection chapter={chapter} t={t} />
            )}

            {isEnded && (
              <ResetMemorySection
                resetMemory={resetMemory}
                t={t}
                buttonLabel={t("higurashi:button.restart")}
                highlight
                mode="restart"
              />
            )}

            {isExpired && (
              <StyledButton
                variant="contained"
                sx={{ mt: 4 }}
                onClick={handleNavigateTickets}
              >
                {t("common:button.purchase_episode")}
              </StyledButton>
            )}

            {/* 1. 次のフェーズ*/}
            {canShowManualAdvanceButton && (
              <NextPhaseSection nextPhase={nextPhase} control={nextPhaseControl} />
            )}
            {/* 1. 使い方 */}
            <StyledButton
              variant="contained"
              sx={{ mt: 4 }}
              onClick={handleNavigateHowToUse}
            >
              {t("common:Footer.how_to_play")}
            </StyledButton>

            {/* 3. メール設定 */}
            <StyledButton
              variant="contained"
              sx={{ mt: 4 }}
              onClick={handleNavigateEmailSettings}
            >
              {t("common:button.email_settings")}
            </StyledButton>

            {/* 4. リセット */}
            {!isEnded && (
              <ResetMemorySection
                resetMemory={resetMemory}
                t={t}
                buttonLabel={t("higurashi:button.reset")}
              />
            )}

            {/* 5. 他のキャラクター */}
            <OtherCharactersButton chapter={chapter} souls={otherSouls} />

            {/* 6. クエスト一覧 */}
            <StyledButton
              variant="contained"
              sx={{ mt: 4 }}
              onClick={handleNavigateQuests}
            >
              {t("common:button.quests")}
            </StyledButton>

            {requestFormUrl && (
              <StyledButton
                variant="contained"
                sx={{ mt: 4 }}
                onClick={handleRequestForm}
              >
                {t("common:button.request_form")}
              </StyledButton>
            )}

            {/* 7. チャットに戻る */}
            <StyledButton
              variant="contained"
              sx={{ mt: 4, mb: 4 }}
              onClick={handleCloseWindow}
            >
              {t("common:button.back_to_chat")}
            </StyledButton>
          </Stack>
        </Fade>
      </Box>
    </AppLayout>
  );
}

export default MyPage;
